/**
 * Sentry initialization for the HiroJet React frontend.
 *
 * Imported as the FIRST statement in src/index.js so global error
 * handlers are installed before any rendering happens.
 *
 * PII redaction (per /app/SECURITY_POLICY.md §11):
 *   - sendDefaultPii: false
 *   - User context limited to id (NEVER email / name).
 *   - beforeSend walks the event tree and scrubs email / phone /
 *     bearer-token shapes from URLs, breadcrumbs, exception messages.
 *   - Session Replay (when enabled) masks all text + media so we never
 *     record candidate names, emails, or resume content visually.
 *
 * Graceful no-op when REACT_APP_SENTRY_DSN is empty/missing — the
 * frontend never depends on Sentry being live to render.
 */
import * as Sentry from '@sentry/react';

const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
const PHONE_RE = /\+?\d{1,3}[\s-]?\(?\d{3,5}\)?[\s-]?\d{3,4}[\s-]?\d{3,5}/g;
const JWT_RE = /eyJ[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]{6,}/g;
const BEARER_RE = /Bearer\s+[A-Za-z0-9_.-]{16,}/gi;

function scrubString(value) {
  if (typeof value !== 'string') return value;
  return value
    .replace(JWT_RE, '[REDACTED_JWT]')
    .replace(BEARER_RE, 'Bearer [REDACTED]')
    .replace(EMAIL_RE, '[REDACTED_EMAIL]')
    .replace(PHONE_RE, '[REDACTED_PHONE]');
}

const SENSITIVE_KEYS = new Set([
  'email', 'phone', 'phone_number', 'password', 'name', 'full_name',
  'first_name', 'last_name', 'candidate_name', 'resume', 'resume_text',
  'resume_content', 'cover_letter', 'message', 'body', 'subject',
  'notes', 'note', 'comment', 'comments',
  'access_token', 'refresh_token', 'auth_token', 'api_key', 'secret', 'token',
  'candidate_email', 'lead_email', 'to_email', 'from_email',
]);

function scrubValue(value, depth = 0) {
  if (depth > 8) return '[REDACTED_TRUNCATED]';
  if (typeof value === 'string') return scrubString(value);
  if (Array.isArray(value)) return value.map(v => scrubValue(v, depth + 1));
  if (value && typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      if (SENSITIVE_KEYS.has(k.toLowerCase())) {
        out[k] = '[REDACTED]';
        continue;
      }
      out[k] = scrubValue(v, depth + 1);
    }
    return out;
  }
  return value;
}

function beforeSend(event) {
  try {
    // 1. User context — keep id only.
    if (event.user) {
      const id = event.user.id;
      event.user = id ? { id } : {};
    }

    // 2. Request — drop body / cookies; scrub URL.
    if (event.request) {
      delete event.request.cookies;
      delete event.request.data;
      if (event.request.headers) {
        const h = event.request.headers;
        for (const k of Object.keys(h)) {
          const lk = k.toLowerCase();
          if (lk === 'authorization' || lk === 'cookie' || lk === 'x-api-key' || lk === 'x-auth-token') {
            h[k] = '[REDACTED]';
          } else if (typeof h[k] === 'string') {
            h[k] = scrubString(h[k]);
          }
        }
      }
      if (typeof event.request.url === 'string') event.request.url = scrubString(event.request.url);
      if (typeof event.request.query_string === 'string') event.request.query_string = scrubString(event.request.query_string);
    }

    // 3. Breadcrumbs.
    if (Array.isArray(event.breadcrumbs)) {
      event.breadcrumbs = event.breadcrumbs.map(c => {
        if (typeof c.message === 'string') c.message = scrubString(c.message);
        if (c.data) c.data = scrubValue(c.data);
        return c;
      });
    }

    // 4. Exception messages + stack-trace local vars.
    if (event.exception && Array.isArray(event.exception.values)) {
      for (const exc of event.exception.values) {
        if (typeof exc.value === 'string') exc.value = scrubString(exc.value);
        if (exc.stacktrace && Array.isArray(exc.stacktrace.frames)) {
          for (const f of exc.stacktrace.frames) {
            if (f.vars) f.vars = scrubValue(f.vars);
          }
        }
      }
    }

    // 5. Top-level scalars + extras + tags.
    if (typeof event.message === 'string') event.message = scrubString(event.message);
    if (event.extra) event.extra = scrubValue(event.extra);
    if (event.tags) {
      for (const k of Object.keys(event.tags)) {
        if (typeof event.tags[k] === 'string') event.tags[k] = scrubString(event.tags[k]);
      }
    }
  } catch (_e) {
    // Never let scrubbing crash the app — drop the event to be safe.
    return null;
  }
  return event;
}

function detectEnvironment() {
  const explicit = (process.env.REACT_APP_SENTRY_ENVIRONMENT || '').trim();
  if (explicit) return explicit;
  if (typeof window !== 'undefined' && window.location) {
    const host = window.location.hostname || '';
    if (host === 'hirojet.com' || host.endsWith('.hirojet.com')) return 'production';
    if (host.includes('preview.emergentagent') || host.includes('staging')) return 'staging';
  }
  return 'development';
}

export function initSentry() {
  const dsn = (process.env.REACT_APP_SENTRY_DSN || '').trim();
  if (!dsn) {
    // Quiet — we don't want the marketing page console flooded.
    return false;
  }
  try {
    const release = (process.env.REACT_APP_SENTRY_RELEASE || '').trim() || undefined;
    Sentry.init({
      dsn,
      environment: detectEnvironment(),
      release,
      sendDefaultPii: false,
      tracesSampleRate: parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || '0.1'),
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: parseFloat(process.env.REACT_APP_SENTRY_REPLAY_ERROR_SAMPLE_RATE || '0.1'),
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
          maskAllInputs: true,
        }),
      ],
      beforeSend,
      maxBreadcrumbs: 50,
    });
    return true;
  } catch (_e) {
    return false;
  }
}
